import { useCallback, useMemo, useRef, useState } from 'react';

/**
 * Inline required-field validation for form dialogs.
 *
 *   const fields = useFieldErrors();
 *   <TextField {...fields.props('name')} onChange={(e) => { fields.clear('name'); ... }} />
 *
 *   if (!fields.validate({ name: !name.trim() && 'Name is required' })) return;
 *
 * validate() takes `{ field: message-or-falsy }` in on-screen order, shows each message
 * under its field, focuses the first invalid one, and returns true when all passed.
 * Call reset() when the dialog opens so a previous attempt's errors don't greet the next.
 */
export default function useFieldErrors() {
  const [errors, setErrors] = useState({});
  const inputs = useRef({});

  const validate = useCallback((checks) => {
    const next = {};
    Object.entries(checks).forEach(([field, message]) => {
      if (message) next[field] = message;
    });
    setErrors(next);
    const first = Object.keys(next)[0];
    if (first) inputs.current[first]?.focus?.();
    return !first;
  }, []);

  const clear = useCallback((field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const reset = useCallback(() => setErrors({}), []);

  // Memoized so `fields` can sit in an effect's dependency list without re-running it
  // on every render; it changes only when the set of errors does.
  return useMemo(() => ({
    errors,
    validate,
    clear,
    reset,
    // For a TextField (or an Autocomplete's renderInput TextField). Pass `helperText`
    // to keep a field's normal hint visible when it has no error.
    props: (field, helperText) => ({
      error: !!errors[field],
      helperText: errors[field] || helperText,
      inputRef: (el) => { inputs.current[field] = el; },
    }),
  }), [errors, validate, clear, reset]);
}
